import { useState, useEffect } from "react";

const AboutSection = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative overflow-hidden py-10 lg:px-20 sm:px-8 px-4">
      {/* Decorative BG Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#CC3316]/20 rounded-full opacity-30 blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#CC3316]/20 rounded-full opacity-20 blur-2xl translate-y-1/2 -translate-x-1/2 pointer-events-none" />

      <div className=" mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">

          {/* ── LEFT: Visual Cards Column ── */}
          <div
            className={`w-full lg:w-1/2 relative flex justify-center transition-all duration-700 ease-out ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
            }`}
          >
            {/* Main image card */}
            <div className="relative w-full">
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-white">
                <img
                  src="https://tfipost.com/wp-content/uploads/2023/01/Maa-Tarini-Temple-building.jpg"
                  alt="Travel"
                  className="w-full h-auto object-cover"
                />
              </div>

              {/* All Listings card - top left overlay */}
              <div className="absolute -top-5 -left-5 bg-[#CC3316]  text-white rounded-2xl p-4 shadow-xl w-44">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="text-xs font-semibold">All Listings</span>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <div className="w-6 h-6 bg-white/20 rounded-full" />
                  <a
                    href="#"
                    className="text-xs bg-yellow-400 text-gray-900 font-semibold px-2 py-1 rounded-lg hover:bg-yellow-300 transition-colors"
                  >
                    Add Listing
                  </a>
                </div>
              </div>

              {/* Earnings card - bottom right overlay */}
              <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl p-3 shadow-xl flex items-center gap-3 min-w-[205px]">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400 mb-0.5">Today's Earnings</p>
                  <p className="text-base font-bold text-gray-800">₹2,500</p>
                </div>
                <a
                  href="#"
                  className="text-xs bg-teal-500 text-white font-semibold px-3 py-1.5 rounded-lg hover:bg-teal-600 transition-colors"
                >
                  Withdraw
                </a>
              </div>
            </div>
          </div>

          {/* ── RIGHT: Content Column ── */}
          <div
            className={`w-full lg:w-1/2 transition-all duration-700 delay-200 ease-out ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            }`}
          >
            {/* About Content */}
            <div className="mb-8">
              <p className="text-[#CC3316] text-sm font-semibold uppercase tracking-widest mb-3">
                About DreamsTour
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight mb-4">
                All-in-one platform for{" "}
                <span className="text-[#CC3316]">unforgettable</span> travel experiences!
              </h2>
              <p className="text-gray-500 leading-relaxed mb-8">
                At DreamsTour, we believe that travel should be simple, seamless, and tailored to
                your needs. Whether you're dreaming of an exotic beach getaway, a cultural city
                tour, or a luxury cruise, we are here to turn your travel dreams into reality.
              </p>

              {/* Feature Items */}
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-[#CC3316] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-indigo-200">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 leading-relaxed pt-1">
                    Clients navigate their journeys, whether for travel or educational purposes,
                    primarily in <span className="font-semibold text-gray-800">Odisha and Domestic Tours.</span>
                  </p>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-amber-400 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-200">
                    <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </div>
                  <p className="text-gray-600 leading-relaxed pt-1">
                    Provides a range of services from{" "}
                    <span className="font-semibold text-gray-800">immigration advice</span> to
                    study-abroad support and vacation planning.
                  </p>
                </div>
              </div>
            </div>

            {/* Mission Box */}
            <div className="bg-white border border-indigo-100 rounded-2xl p-6 shadow-sm">
              <p className="text-[#CC3316] text-sm font-semibold uppercase tracking-widest mb-2">
                Our Mission
              </p>
              <p className="text-gray-500 leading-relaxed">
                Our mission is to make travel more accessible, enjoyable, and hassle-free for
                everyone. With our range of services, we're committed to crafting experiences that
                leave a lasting impression.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;